import { useState, useMemo, useRef, useEffect } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels)

interface MarcaItem {
  id: string
  nombre: string
  total: number
  coincidencias: number
  porcentaje: number
}

interface RegionItem {
  region: string
  total: number
  coincidencias: number
  sin_coincidencia: number
  porcentaje: number
}

interface SegmentoItem {
  marca: string
  pais: string
  total: number
  coincidencias: number
  sin_coincidencia: number
  porcentaje: number
  regiones: RegionItem[]
}

interface PaisItem {
  pais: string
  total: number
  coincidencias: number
  sin_coincidencia: number
  porcentaje: number
}

interface AllegraBilaxtenData {
  titulo: string
  subtitulo: string
  total_general: number
  total_coincidencias: number
  total_sin_coincidencia: number
  porcentaje_coincidencia: number
  total_marcas: number
  total_paises: number
  total_regiones: number
  marcas: MarcaItem[]
  paises: PaisItem[]
  segmentos: SegmentoItem[]
}

const REGIONES_LIMIT = 10

const colores = {
  verde: { bg: 'rgba(34, 197, 94, 0.8)', border: '#22c55e' },
  rojo: { bg: 'rgba(239, 68, 68, 0.8)', border: '#ef4444' },
}

export function AllegraBilaxtenPage() {
  const { data, isLoading, error } = useDashReport<AllegraBilaxtenData>('opella/allegra-bilaxten')
  const [filtroMarca, setFiltroMarca] = useState<string>('todos')
  const [filtroPais, setFiltroPais] = useState<string>('todos')
  const [regionesExpandidas, setRegionesExpandidas] = useState(false)
  const chartMarcaRef = useRef<ChartJS<'bar'> | null>(null)
  const chartPaisRef = useRef<ChartJS<'bar'> | null>(null)

  // Reset pais filter when marca changes
  useEffect(() => {
    setFiltroPais('todos')
  }, [filtroMarca])

  // Derived data based on filters
  const resumen = useMemo(() => {
    if (!data) return null
    if (filtroMarca === 'todos') {
      return {
        titulo: 'Resumen General',
        totalBase: data.total_general,
        totalCoincidencias: data.total_coincidencias,
        numMarcas: data.marcas.length,
        numPaises: data.paises.length,
        numRegiones: data.total_regiones,
      }
    }
    const marca = data.marcas.find((m) => m.id === filtroMarca)
    if (!marca) return null
    const segmentosMarca = data.segmentos.filter(
      (s) => s.marca.toLowerCase().replace(/ /g, '_') === filtroMarca,
    )
    const paisesUnicos = [...new Set(segmentosMarca.map((s) => s.pais))]

    if (filtroPais !== 'todos') {
      const seg = segmentosMarca.find((s) => s.pais === filtroPais)
      if (seg) {
        return {
          titulo: `Resumen ${seg.marca} - ${filtroPais}`,
          totalBase: seg.total,
          totalCoincidencias: seg.coincidencias,
          numMarcas: 1,
          numPaises: 1,
          numRegiones: seg.regiones.length,
        }
      }
    }

    const totalRegiones = segmentosMarca.reduce((acc, s) => acc + s.regiones.length, 0)
    return {
      titulo: `Resumen ${marca.nombre}`,
      totalBase: marca.total,
      totalCoincidencias: marca.coincidencias,
      numMarcas: 1,
      numPaises: paisesUnicos.length,
      numRegiones: totalRegiones,
    }
  }, [data, filtroMarca, filtroPais])

  const paisesParaMarca = useMemo(() => {
    if (!data || filtroMarca === 'todos') return []
    const segmentosMarca = data.segmentos.filter(
      (s) => s.marca.toLowerCase().replace(/ /g, '_') === filtroMarca,
    )
    return [...new Set(segmentosMarca.map((s) => s.pais))]
  }, [data, filtroMarca])

  // Chart data for marca
  const chartMarcaData = useMemo(() => {
    if (!data) return null
    let marcasFiltradas: MarcaItem[]
    if (filtroMarca === 'todos') {
      marcasFiltradas = data.marcas
    } else {
      marcasFiltradas = data.marcas.filter(
        (m) => m.id === filtroMarca,
      )
    }
    return {
      labels: marcasFiltradas.map((m) => m.nombre),
      datasets: [
        {
          label: 'Coincidencias',
          data: marcasFiltradas.map((m) => m.coincidencias),
          backgroundColor: colores.verde.bg,
          borderColor: colores.verde.border,
          borderWidth: 0,
          borderRadius: 0,
          borderSkipped: false as const,
        },
        {
          label: 'Sin Coincidencia',
          data: marcasFiltradas.map((m) => m.total - m.coincidencias),
          backgroundColor: colores.rojo.bg,
          borderColor: colores.rojo.border,
          borderWidth: 0,
          borderRadius: { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 },
          borderSkipped: false as const,
        },
      ],
    }
  }, [data, filtroMarca])

  // Chart data for pais
  const chartPaisData = useMemo(() => {
    if (!data) return null
    let paisesFiltrados: { pais: string; coincidencias: number; sin_coincidencia: number }[]

    if (filtroMarca === 'todos') {
      paisesFiltrados = data.paises
    } else {
      const segmentosMarca = data.segmentos.filter(
        (s) => s.marca.toLowerCase().replace(/ /g, '_') === filtroMarca,
      )
      if (filtroPais !== 'todos') {
        const seg = segmentosMarca.find((s) => s.pais === filtroPais)
        paisesFiltrados = seg
          ? [{ pais: seg.pais, coincidencias: seg.coincidencias, sin_coincidencia: seg.sin_coincidencia }]
          : []
      } else {
        paisesFiltrados = segmentosMarca.map((s) => ({
          pais: s.pais,
          coincidencias: s.coincidencias,
          sin_coincidencia: s.sin_coincidencia,
        }))
      }
    }

    return {
      labels: paisesFiltrados.map((p) => p.pais),
      datasets: [
        {
          label: 'Coincidencias',
          data: paisesFiltrados.map((p) => p.coincidencias),
          backgroundColor: colores.verde.bg,
          borderColor: colores.verde.border,
          borderWidth: 0,
          borderRadius: 0,
          borderSkipped: false as const,
        },
        {
          label: 'Sin Coincidencia',
          data: paisesFiltrados.map((p) => p.sin_coincidencia),
          backgroundColor: colores.rojo.bg,
          borderColor: colores.rojo.border,
          borderWidth: 0,
          borderRadius: { topLeft: 0, topRight: 6, bottomLeft: 0, bottomRight: 6 },
          borderSkipped: false as const,
        },
      ],
    }
  }, [data, filtroMarca, filtroPais])

  // Filtered segment cards
  const filteredSegmentos = useMemo(() => {
    if (!data) return []
    return data.segmentos.filter((s) => {
      const marcaMatch =
        filtroMarca === 'todos' || s.marca.toLowerCase().replace(/ /g, '_') === filtroMarca
      const paisMatch = filtroPais === 'todos' || s.pais === filtroPais
      return marcaMatch && paisMatch
    })
  }, [data, filtroMarca, filtroPais])

  // Region rows (filtered + limited)
  const allRegionRows = useMemo(() => {
    if (!data) return []
    const rows: { marca: string; pais: string; region: RegionItem }[] = []
    for (const seg of data.segmentos) {
      const marcaMatch =
        filtroMarca === 'todos' || seg.marca.toLowerCase().replace(/ /g, '_') === filtroMarca
      const paisMatch = filtroPais === 'todos' || seg.pais === filtroPais
      if (marcaMatch && paisMatch) {
        for (const region of seg.regiones) {
          rows.push({ marca: seg.marca, pais: seg.pais, region })
        }
      }
    }
    return rows
  }, [data, filtroMarca, filtroPais])

  const visibleRegionRows = useMemo(() => {
    if (regionesExpandidas || allRegionRows.length <= REGIONES_LIMIT) return allRegionRows
    return allRegionRows.slice(0, REGIONES_LIMIT)
  }, [allRegionRows, regionesExpandidas])

  const regionesContador = useMemo(() => {
    const total = allRegionRows.length
    if (regionesExpandidas || total <= REGIONES_LIMIT) return `(${total})`
    return `(${Math.min(REGIONES_LIMIT, total)} de ${total})`
  }, [allRegionRows, regionesExpandidas])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data || !resumen) {
    return (
      <div className="bg-gray-100 p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  const totalSin = resumen.totalBase - resumen.totalCoincidencias
  const pctCoincidencia =
    resumen.totalBase > 0 ? ((resumen.totalCoincidencias / resumen.totalBase) * 100).toFixed(2) : '0'
  const pctSin =
    resumen.totalBase > 0 ? ((totalSin / resumen.totalBase) * 100).toFixed(0) : '0'

  return (
    <div className="bg-gray-100 p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img
                src="/logo-tonal-negro.png"
                alt="Tonal"
                className="h-8 lg:h-10 w-auto mr-4 lg:mr-6"
              />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">
                  {data.titulo}
                </h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <div className="flex items-center">
              <a
                href="https://enterprise.tonalstrategies.com/pricing/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs lg:text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm w-full lg:w-auto justify-center"
              >
                Calculador
                <svg
                  className="w-3 h-3 lg:w-4 lg:h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-blue-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              <span className="font-semibold text-gray-900">Filtrar por Marca:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFiltroMarca('todos')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  filtroMarca === 'todos'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todas
              </button>
              {data.marcas.map((marca) => (
                <button
                  key={marca.id}
                  onClick={() => setFiltroMarca(marca.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filtroMarca === marca.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {marca.nombre} <span className="text-xs opacity-75">({marca.total})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Filtro de País */}
          {filtroMarca !== 'todos' && paisesParaMarca.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-green-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="font-semibold text-gray-900">País:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setFiltroPais('todos')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      filtroPais === 'todos'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Todos
                  </button>
                  {paisesParaMarca.map((pais) => {
                    const seg = data.segmentos.find(
                      (s) =>
                        s.marca.toLowerCase().replace(/ /g, '_') === filtroMarca && s.pais === pais,
                    )
                    return (
                      <button
                        key={pais}
                        onClick={() => setFiltroPais(pais)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          filtroPais === pais
                            ? 'bg-emerald-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {pais}{' '}
                        <span className="text-xs opacity-75">({seg?.total ?? 0})</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Resumen General */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg
              className="w-5 h-5 text-blue-600 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <span>{resumen.titulo}</span>
          </h2>

          {/* Fila principal */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">
                {resumen.totalBase.toLocaleString('es-EC')}
              </p>
              <p className="text-sm text-gray-600 mt-1">Total Base</p>
              <p className="text-xs text-gray-500 font-semibold mt-1">100%</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-green-600">
                {resumen.totalCoincidencias.toLocaleString('es-EC')}
              </p>
              <p className="text-sm text-gray-600 mt-1">Con Coincidencia</p>
              <p className="text-xs text-green-600 font-semibold mt-1">{pctCoincidencia}%</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-500">
                {totalSin.toLocaleString('es-EC')}
              </p>
              <p className="text-sm text-gray-600 mt-1">Sin Coincidencia</p>
              <p className="text-xs text-gray-500 font-semibold mt-1">{pctSin}%</p>
            </div>
          </div>

          {/* Fila secundaria */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/60 rounded-lg px-3 py-2 text-center">
              <p className="text-xl font-bold text-blue-600">{resumen.numMarcas}</p>
              <p className="text-xs text-gray-500">Marcas</p>
            </div>
            <div className="bg-white/60 rounded-lg px-3 py-2 text-center">
              <p className="text-xl font-bold text-green-600">{resumen.numPaises}</p>
              <p className="text-xs text-gray-500">Países</p>
            </div>
            <div className="bg-white/60 rounded-lg px-3 py-2 text-center">
              <p className="text-xl font-bold text-purple-600">{resumen.numRegiones}</p>
              <p className="text-xs text-gray-500">Regiones</p>
            </div>
          </div>
        </div>

        {/* Graficos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Grafico por Marca */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <svg
                className="w-5 h-5 text-blue-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                />
              </svg>
              Coincidencias por Marca
            </h3>
            <div className="h-64 lg:h-72">
              {chartMarcaData && (
                <Bar
                  ref={chartMarcaRef}
                  data={chartMarcaData}
                  plugins={[ChartDataLabels]}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top' as const,
                        labels: {
                          usePointStyle: true,
                          pointStyle: 'rectRounded',
                          padding: 15,
                        },
                      },
                      datalabels: {
                        color: '#fff',
                        font: { weight: 500, size: 11 },
                        formatter: (value: number) =>
                          value > 0 ? value.toLocaleString('es-EC') : '',
                      },
                    },
                    scales: {
                      x: { stacked: true, grid: { display: false } },
                      y: { stacked: true, grid: { color: '#f3f4f6' } },
                    },
                  }}
                />
              )}
            </div>
          </div>

          {/* Grafico por Pais */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <svg
                className="w-5 h-5 text-green-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Coincidencias por País
            </h3>
            <div className="h-64 lg:h-72">
              {chartPaisData && (
                <Bar
                  ref={chartPaisRef}
                  data={chartPaisData}
                  plugins={[ChartDataLabels]}
                  options={{
                    indexAxis: 'y' as const,
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top' as const,
                        labels: {
                          usePointStyle: true,
                          pointStyle: 'rectRounded',
                          padding: 15,
                        },
                      },
                      datalabels: {
                        color: '#fff',
                        font: { weight: 500, size: 10 },
                        formatter: (value: number) =>
                          value > 100 ? value.toLocaleString('es-EC') : '',
                      },
                    },
                    scales: {
                      x: { stacked: true, grid: { color: '#f3f4f6' } },
                      y: { stacked: true, grid: { display: false } },
                    },
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Tarjetas por Marca + Pais */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-6 flex items-center">
            <svg
              className="w-5 h-5 text-indigo-600 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            Detalle por Marca y País
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {filteredSegmentos.map((seg, idx) => (
              <div
                key={`${seg.marca}-${seg.pais}-${idx}`}
                className="bg-gray-50 rounded-xl p-5 border border-gray-100 transition-all duration-300 hover:scale-[1.02]"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span
                      className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                        seg.marca === 'Allegra'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}
                    >
                      {seg.marca}
                    </span>
                    <h4 className="text-lg font-bold text-gray-900 mt-2">{seg.pais}</h4>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-2xl font-bold ${
                        seg.porcentaje >= 70
                          ? 'text-green-600'
                          : seg.porcentaje >= 50
                            ? 'text-yellow-600'
                            : 'text-red-600'
                      }`}
                    >
                      {seg.porcentaje}%
                    </p>
                    <p className="text-xs text-gray-500">coincidencia</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>{seg.coincidencias} coinciden</span>
                    <span>{seg.sin_coincidencia} no coinciden</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${
                        seg.porcentaje >= 70
                          ? 'bg-green-500'
                          : seg.porcentaje >= 50
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                      }`}
                      style={{ width: `${seg.porcentaje}%` }}
                    />
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-lg font-bold text-gray-900">{seg.total}</p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-lg font-bold text-green-600">{seg.coincidencias}</p>
                    <p className="text-xs text-gray-500">Si</p>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-lg font-bold text-red-600">{seg.sin_coincidencia}</p>
                    <p className="text-xs text-gray-500">No</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detalle por Regiones */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900 flex items-center">
              <svg
                className="w-5 h-5 text-purple-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Detalle por Regiones
              <span className="ml-2 text-sm font-normal text-gray-500">{regionesContador}</span>
            </h3>
            <button
              onClick={() => setRegionesExpandidas(!regionesExpandidas)}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Ver todas"
            >
              <svg
                className={`w-5 h-5 transition-transform duration-300 ease-in-out ${regionesExpandidas ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Marca</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">País</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Región</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Total</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Coinciden</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">
                    No Coinciden
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">%</th>
                </tr>
              </thead>
              <tbody>
                {visibleRegionRows.map((row, idx) => (
                  <tr
                    key={`${row.marca}-${row.pais}-${row.region.region}-${idx}`}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-2 px-4">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${
                          row.marca === 'Allegra'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}
                      >
                        {row.marca}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-gray-700">{row.pais}</td>
                    <td className="py-2 px-4 text-gray-900 font-medium">{row.region.region}</td>
                    <td className="py-2 px-4 text-right text-gray-700">{row.region.total}</td>
                    <td className="py-2 px-4 text-right text-green-600 font-medium">
                      {row.region.coincidencias}
                    </td>
                    <td className="py-2 px-4 text-right text-red-600 font-medium">
                      {row.region.sin_coincidencia}
                    </td>
                    <td className="py-2 px-4 text-right">
                      <span
                        className={`font-semibold ${
                          row.region.porcentaje >= 70
                            ? 'text-green-600'
                            : row.region.porcentaje >= 50
                              ? 'text-yellow-600'
                              : 'text-red-600'
                        }`}
                      >
                        {row.region.porcentaje}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-12 py-6 text-center">
        <div className="text-xs text-gray-400">
          <a
            href="https://enterprise.tonalstrategies.com"
            className="hover:text-gray-600 transition-colors"
          >
            Tonal Enterprise
          </a>
          <span className="mx-2">&bull;</span>
          <span>2025 Tonal Strategies</span>
        </div>
      </footer>
    </div>
  )
}
